<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  IconChat,
  IconDocumentText,
  IconFolder,
  IconLogout,
  IconPlay,
} from '@/components/design/icons';
import logoUrl from '@/assets/logo.svg';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaSegmented, {
  type GaiaSegmentedOption,
} from '@/components/design/base/GaiaSegmented.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTooltip from '@/components/design/base/GaiaTooltip.vue';
import { gaiaNeutral } from '@/components/design/themes/tokens';
import { useAuthStore } from '@/stores/auth';
import { useUserAttentionStore } from '@/stores/userAttentionStore';
import { useOrganizationStore } from '@/stores/organizationStore';
import { clearStoredOfficeAuthToken } from '@/office/utils';
import { logger } from '@/utils/logger';

/**
 * The task pane's chrome: the mark and the sign-out at the top, and the
 * feature switcher pinned to the bottom. The switcher is the design system's
 * segmented control — the same pill track the web uses for a view-mode
 * switch — because a 350px pane has no room for a sidebar and its four
 * destinations are peers, not a hierarchy.
 */

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const userAttentionStore = useUserAttentionStore();
const organizationStore = useOrganizationStore();

const officeManifestVersion = (import.meta.env.VITE_OFFICE_ADDIN_MANIFEST_VERSION ?? '').trim();
// eslint-disable-next-line sonarjs/no-hardcoded-ip -- an Office add-in manifest version, not an IP address
const showOfficeManifestBeta = officeManifestVersion === '1.0.0.0';

type OfficeTab = 'file-vault' | 'gaia-chat' | 'office-playbooks' | 'office-templates';

const activeOfficeTab = computed<OfficeTab>(() =>
  route.path.startsWith('/gaia-chat')
    ? 'gaia-chat'
    : route.path.startsWith('/office-playbooks')
      ? 'office-playbooks'
      : route.path.startsWith('/office-templates')
        ? 'office-templates'
        : 'file-vault'
);

const OFFICE_TAB_PATHS: Record<OfficeTab, string> = {
  'file-vault': '/vault',
  'gaia-chat': '/gaia-chat',
  'office-playbooks': '/office-playbooks',
  'office-templates': '/office-templates',
};

const tabOptions = computed<GaiaSegmentedOption[]>(() => {
  const options: GaiaSegmentedOption[] = [
    { label: 'Vault', value: 'file-vault', icon: IconFolder },
    {
      label: 'Chat',
      value: 'gaia-chat',
      icon: IconChat,
      // Mirrors the sidebar's attention dot: a thread is waiting on the user.
      tone: userAttentionStore.generalCount > 0 ? 'alert' : undefined,
    },
  ];
  if (authStore.userPermissions.can_use_playbooks) {
    options.push({ label: 'Playbooks', value: 'office-playbooks', icon: IconPlay });
  }
  if (authStore.userPermissions.can_use_contract_generation) {
    options.push({ label: 'Templates', value: 'office-templates', icon: IconDocumentText });
  }
  return options;
});

function isOfficeTab(value: string): value is OfficeTab {
  return Object.hasOwn(OFFICE_TAB_PATHS, value);
}

function selectOfficeTab(value: string): void {
  if (!isOfficeTab(value) || value === activeOfficeTab.value) return;
  const targetPath = OFFICE_TAB_PATHS[value];

  // Mirrors AppSidebar: while a chat thread is waiting on the user, the dot
  // they just clicked should lead to the thread list — a blank new chat would
  // bury the waiting thread.
  if (value === 'gaia-chat' && userAttentionStore.generalCount > 0) {
    void router.push({ path: targetPath, query: { thread_list: Date.now().toString() } });
    return;
  }

  void router.push(targetPath);
}

async function handleOfficeSignOut(): Promise<void> {
  try {
    await authStore.logout();
    clearStoredOfficeAuthToken();
    await router.push('/office-login');
  } catch (error) {
    logger.error('Office sign-out failed:', error);
  }
}

// In the Office task pane neither the sidebar nor the topbar render, so
// nothing else loads the active organization — fetch it here to know the
// feature flags (e.g. whether to show the Templates tab). This component
// only mounts when the user is authenticated.
onMounted(async (): Promise<void> => {
  if (!organizationStore.activeOrganization) {
    try {
      await organizationStore.fetchActiveOrganization();
    } catch (error) {
      logger.error('Failed to fetch active organization:', error);
    }
  }
});

const surface = gaiaNeutral.surface;
const rule = gaiaNeutral.borderDivider;
</script>

<template>
  <!-- Three columns with equal side tracks, so the mark sits on the pane's
       true centre whatever the sign-out and the version tag measure. -->
  <header class="office-header">
    <gaia-tooltip placement="bottom-start">
      <template #trigger>
        <gaia-icon-button
          :dimension="30"
          quaternary
          aria-label="Sign out"
          :loading="authStore.loading"
          @click="handleOfficeSignOut"
        >
          <n-icon :size="18" :component="IconLogout" />
        </gaia-icon-button>
      </template>
      Sign out
    </gaia-tooltip>

    <img :src="logoUrl" alt="GAIA" class="office-header__logo" />

    <n-flex
      v-if="officeManifestVersion"
      align="center"
      justify="end"
      :size="6"
      :wrap="false"
      :style="{ minWidth: 0 }"
    >
      <gaia-text size="micro" tone="muted" :style="{ fontVariantNumeric: 'tabular-nums' }">
        v{{ officeManifestVersion }}
      </gaia-text>
      <gaia-tag v-if="showOfficeManifestBeta" variant="amber" size="tiny">Beta</gaia-tag>
    </n-flex>
    <span v-else />
  </header>

  <!-- Pinned under the content: the pane scrolls, the switcher does not. -->
  <nav class="office-bottom-nav" aria-label="Office add-in features">
    <gaia-segmented
      block
      stacked
      :value="activeOfficeTab"
      :options="tabOptions"
      @update:value="selectOfficeTab"
    />
  </nav>
</template>

<style scoped>
/* Header and pinned footer are layout the design system has no primitive for
   in a task pane: the header's centred mark needs a 1fr auto 1fr grid, and the
   switcher has to stay fixed while the routed page scrolls under it. */
.office-header {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  height: 48px;
  padding: 0 10px;
  background: v-bind(surface);
  border-bottom: 1px solid v-bind(rule);
}

.office-header__logo {
  height: 18px;
  width: auto;
  justify-self: center;
}

.office-bottom-nav {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
  padding: 8px 10px calc(8px + env(safe-area-inset-bottom));
  background: v-bind(surface);
  border-top: 1px solid v-bind(rule);
}
</style>
